/**
 * Smart NLP question pool for the property page.
 * Questions are categorised into 4 buckets.
 * On each page load we pick ONE question from each bucket per section,
 * ensuring the two sections on the page never show the same question.
 *
 * Each question string is used as the translation key in translations.json.
 * The English text itself is the key — t() resolves it to the current locale.
 */

export interface QuestionBucket {
  id: string;
  label: string;
  questions: string[];
}

export const QUESTION_BUCKETS: QuestionBucket[] = [
  {
    id: "availability",
    label: "Availability & Fit",
    questions: [
      "When is the earliest I can move in?",
      "What room types are available here?",
      "What's the minimum stay duration?",
      "Is this room available for dual occupancy?",
    ],
  },
  {
    id: "money",
    label: "Money",
    questions: [
      "What's the total monthly cost with all fees?",
      "How much is the deposit and when do I pay it?",
      "Can I pay in instalments?",
      "Are bills and Wi-Fi included in the rent?",
    ],
  },
  {
    id: "policies",
    label: "Policies & Risk",
    questions: [
      "What's the cancellation and refund policy?",
      "Will I get a refund if my visa is refused?",
      "Do I need a UK guarantor?",
      "Can I see the tenancy agreement before I pay?",
    ],
  },
  {
    id: "property",
    label: "Place & Property",
    questions: [
      "Which universities are close to this property?",
      "Can I book a viewing first?",
      "Is the kitchen or bathroom shared?",
    ],
  },
];

/**
 * Picks 5 random translation keys — one from each of the 4 buckets, plus one extra
 * from a random bucket (ensuring no duplicate within the set).
 * Excludes any keys passed in, so the second section shows different questions.
 *
 * @param excludeQuestions - Questions to exclude (already used by the other section)
 * @returns Array of 5 question keys, shuffled
 */
export const getRandomQuestions = (excludeQuestions: string[] = []): string[] => {
  const selected: string[] = [];

  // We want exactly 6 questions.
  // Index 0 and Index 3 must be from Bucket 1 ("availability").

  // 1. Pick 2 questions from Bucket 1 ("availability")
  const bucket1 = QUESTION_BUCKETS.find((b) => b.id === "availability");
  const availableB1 = bucket1 ? bucket1.questions.filter((q) => !excludeQuestions.includes(q)) : [];

  const selectedB1: string[] = [];
  while (selectedB1.length < 2 && availableB1.length > 0) {
    const rIndex = Math.floor(Math.random() * availableB1.length);
    selectedB1.push(availableB1[rIndex]);
    availableB1.splice(rIndex, 1);
  }

  // 2. Pick 1 question from each of the OTHER buckets
  const availableOthers: string[][] = [];
  const selectedOthers: string[] = [];

  QUESTION_BUCKETS.forEach((bucket) => {
    if (bucket.id === "availability") return;
    const available = bucket.questions.filter(
      (q) => !excludeQuestions.includes(q) && !selectedOthers.includes(q),
    );
    if (available.length > 0) {
      const rIndex = Math.floor(Math.random() * available.length);
      selectedOthers.push(available[rIndex]);
      availableOthers.push(available.filter((_, i) => i !== rIndex));
    } else {
      availableOthers.push([]);
    }
  });

  // 3. We need 4 questions for the "other" slots (indices 1, 2, 4, 5).
  // We currently have 3 (one from each of the 3 other buckets).
  // Pick 1 more from ANY of the remaining questions in the other buckets.
  const allRemaining = availableOthers.flat();
  if (allRemaining.length > 0) {
    const bonusIndex = Math.floor(Math.random() * allRemaining.length);
    selectedOthers.push(allRemaining[bonusIndex]);
  }

  // 4. Shuffle the 'other' questions
  for (let i = selectedOthers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [selectedOthers[i], selectedOthers[j]] = [selectedOthers[j], selectedOthers[i]];
  }

  // 5. Construct the final array of length 6
  // [ B1_1, Other_1, Other_2, B1_2, Other_3, Other_4 ]
  selected[0] = selectedB1[0] || "";
  selected[1] = selectedOthers[0] || "";
  selected[2] = selectedOthers[1] || "";
  selected[3] = selectedB1[1] || "";
  selected[4] = selectedOthers[2] || "";
  selected[5] = selectedOthers[3] || "";

  return selected.filter(Boolean);
};

/**
 * Generates two non-overlapping question sets for the two Smart Question
 * sections on the property page. Each set has one question per bucket (4 total).
 * The second set excludes all questions chosen in the first set.
 */
export const getTwoQuestionSets = (): [string[], string[]] => {
  const firstSet = getRandomQuestions();
  const secondSet = getRandomQuestions(firstSet);
  return [firstSet, secondSet];
};
